import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTeam } from '@/shared/context/TeamContext'
import { analyticsApi } from '../api/analytics.api'
import type { ReporteAnalyticsCreateInput, ReporteAnalyticsUpdateInput } from '../types'

function toSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

export function useAnalyticsNav() {
  const { selectedArea } = useTeam()
  const { data, isLoading } = useQuery({
    queryKey: ['reportes-analytics', selectedArea?.id, { page: 1, page_size: 500 }],
    queryFn: () => analyticsApi.getReportes({ page_size: 500 }),
  })

  const grupos = useMemo(() => {
    if (!data?.results) return []
    const seen = new Map<number, string>()
    for (const r of data.results) {
      if (!seen.has(r.grupo_id)) seen.set(r.grupo_id, r.grupo_nombre)
    }
    return Array.from(seen, ([id, name]) => ({ id, name, slug: toSlug(name) }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [data])

  return { grupos, isLoading }
}

export function useReportesAnalytics(params?: {
  page?: number
  page_size?: number
  search?: string
  grupo_id?: number
  activo?: boolean
}) {
  const { selectedArea } = useTeam()
  return useQuery({
    queryKey: ['reportes-analytics', selectedArea?.id, params],
    queryFn: () => analyticsApi.getReportes(params),
  })
}

export function useCreateReporteAnalytics() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: ReporteAnalyticsCreateInput) =>
      analyticsApi.createReporte(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-analytics'] })
    },
  })
}

export function useUpdateReporteAnalytics() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ReporteAnalyticsUpdateInput }) =>
      analyticsApi.updateReporte(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-analytics'] })
    },
  })
}

export function useDeleteReporteAnalytics() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => analyticsApi.deleteReporte(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-analytics'] })
    },
  })
}

export function useToggleActivoAnalytics() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => analyticsApi.toggleActivo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-analytics'] })
    },
  })
}

export function useGruposDisponiblesAnalytics() {
  return useQuery({
    queryKey: ['grupos-disponibles-analytics'],
    queryFn: () => analyticsApi.getGruposDisponibles(),
  })
}
